import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { AppBar, Box, Container, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Navbar } from '../organisms/layout/navbar/Navbar';
import { Sidebar } from '../organisms/layout/Sidebar/Sidebar';
import { useLocation } from 'react-router';
import { EditPageHeader } from '../organisms/editPage/header/EditPageHeader';
import { useIsDemoUser } from 'src/components/hooks/useIsDemoUser';
import { DemoAccountBanner } from '../molecules/DemoAccountBanner';
import { DemoEditorGuidance } from '../molecules/DemoEditorGuidance';
import { PaymentIssueBanner } from '../organisms/license/PaymentIssueBanner';
import { BillingSuspendedBanner } from '../organisms/license/BillingSuspendedBanner';
import { useReportAppBarHeight } from 'src/utils/demoGuidanceLayout';

export const sidebarMinWidth = 88;
export const workspaceSidebarWidth = 240;

// * sidebar converting from temporary to permanent when window size be grater than "md" breakpoint

type DashboardTemplatePropsType = {
  navbarComponent?: any;
  children?: any;
};

export const DashboardTemplate: FC<DashboardTemplatePropsType> = ({
  navbarComponent,
  children,
}) => {
  const theme = useTheme(),
    { pathname } = useLocation();
  const isDemoUser = useIsDemoUser();

  const isEditPage = useMemo(() => pathname.includes('edit'), [pathname]);
  // The editor gets the full height; the banner stays on the other demo pages.
  const showDemoBanner = isDemoUser && !isEditPage;
  const appBarRef = useRef<HTMLDivElement>(null);
  useReportAppBarHeight(appBarRef, isEditPage);

  const isLight = theme.palette.mode === 'light';
  const { gridLine, heroGlow, gridSize } = theme.palette.backdrop;

  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const [openSandwichMenu, setOpenSandwichMenu] = useState(isMd);

  useEffect(() => {
    !isMd && setOpenSandwichMenu(false);
  }, [isMd]);

  const contentBoxMinHeight = useMemo(() => {
    let result = '0';
    if (isEditPage) {
      result = `calc(100% - ${isMd ? 67.5 : 56}px)`;
    } else {
      result = `calc(100% - ${isMd ? 87.5 : 67}px)`;
    }
    return result;
  }, [isEditPage, isMd]);

  return (
    <Stack
      direction="row"
      sx={{
        height: '99.99vh',
        width: '100vw',
        maxWidth: '100vw',
        overflowX: 'hidden',
        backgroundColor: 'customGrey.background',
        // Light theme only (the tokens are transparent in dark). The editor
        // keeps a plain background: the documents sit on their own canvas.
        ...(isLight && !isEditPage && {
          backgroundImage: [
            `radial-gradient(ellipse 80% 60% at 50% -10%, ${heroGlow}, transparent 70%)`,
            `linear-gradient(to right, ${gridLine} 1px, transparent 1px)`,
            `linear-gradient(to bottom, ${gridLine} 1px, transparent 1px)`,
          ].join(', '),
          backgroundSize: `100% 100%, ${gridSize}, ${gridSize}`,
        }),
      }}
    >
      <Sidebar
        openSandwichMenu={openSandwichMenu}
        setOpenSandwichMenu={setOpenSandwichMenu}
      />
      <Stack
        sx={{
          width: { xs: '100%', md: `calc(100% - ${sidebarMinWidth}px)` },
          height: '100%',
        }}
      >
        <AppBar
          ref={appBarRef}
          elevation={0}
          sx={{
            width: '100%',
            pl: isMd ? `${sidebarMinWidth}px` : 0,
            backgroundColor: isLight
              ? 'rgba(255, 255, 255, 0.88)'
              : theme.palette.customGrey.background,
            ...(isLight && {
              backdropFilter: 'blur(8px)',
              borderBottom: `1px solid ${theme.palette.hairline}`,
            }),
            flex: '0 1 auto',
            // High-frequency layout shifts: keep width/margin transitions short + ease-out
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.duration.shorter,
            }),
          }}
        >
          {showDemoBanner && <DemoAccountBanner />}
          {navbarComponent || isEditPage ? (
            <EditPageHeader setOpenSandwichMenu={setOpenSandwichMenu} />
          ) : (
            <Navbar setOpenSandwichMenu={setOpenSandwichMenu} />
          )}
        </AppBar>
        <Box
          component="main"
          sx={{
            position: 'relative',
            width: '100%',
          }}
        >
          {showDemoBanner && (
            <Box sx={{ visibility: 'hidden' }}>
              <DemoAccountBanner />
            </Box>
          )}
          {isEditPage ? (
            <Box sx={{ visibility: 'hidden' }} aria-hidden>
              <EditPageHeader spacer />
            </Box>
          ) : (
            <Box sx={{ visibility: 'hidden' }}>
              <Navbar setOpenSandwichMenu={setOpenSandwichMenu} />
            </Box>
          )}
          <Container
            sx={{
              minHeight: contentBoxMinHeight,
              p: isEditPage ? '0 !important' : { xs: 2, md: 4 },
              width: '100%',
              overflowX: 'hidden',
            }}
            maxWidth={isEditPage ? false : 'lg'}
          >
            {!isDemoUser && !isEditPage && <BillingSuspendedBanner />}
            {!isDemoUser && !isEditPage && <PaymentIssueBanner />}
            {children}
          </Container>
          {isDemoUser && isEditPage && <DemoEditorGuidance />}
        </Box>
      </Stack>
    </Stack>
  );
};
